import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useViewerWebSocket } from '../hooks/useWebSocket';
import { 
  Wifi, 
  WifiOff, 
  AlertCircle,
  Eye,
  Clock,
  Users,
  LogIn,
  RefreshCw
} from 'lucide-react';
import { cn } from '../lib/utils';

interface Subtitle {
  id: string;
  text: string;
  timestamp: number;
  confidence: number;
  displayTime: number;
}

interface ViewerInterfaceProps {
  className?: string;
}

export const ViewerInterface: React.FC<ViewerInterfaceProps> = ({ className }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [roomId, setRoomId] = useState<string>(searchParams.get('room') || '');
  const [inputRoomId, setInputRoomId] = useState<string>('');
  const [subtitles, setSubtitles] = useState<Subtitle[]>([]);
  const [isJoined, setIsJoined] = useState<boolean>(false);
  const subtitleTimeoutRef = useRef<Map<string, number>>(new Map());

  // WebSocket connection for viewing
  const webSocket = useViewerWebSocket(roomId, {
    autoConnect: false,
    maxReconnectAttempts: 10,
    initialReconnectDelay: 2000
  });

  // Handle new transcriptions
  useEffect(() => {
    if (webSocket.lastTranscription) {
      const transcription = webSocket.lastTranscription;
      const now = Date.now();
      
      const newSubtitle: Subtitle = {
        id: `${transcription.timestamp}-${Math.random()}`,
        text: transcription.text,
        timestamp: transcription.timestamp,
        confidence: transcription.confidence,
        displayTime: now
      };

      setSubtitles(prev => [...prev, newSubtitle]);

      // Set timeout to hide subtitle after 10 seconds
      const timeoutId = setTimeout(() => {
        setSubtitles(prev => prev.filter(sub => sub.id !== newSubtitle.id));
        subtitleTimeoutRef.current.delete(newSubtitle.id);
      }, 10000);

      subtitleTimeoutRef.current.set(newSubtitle.id, timeoutId);
    }
  }, [webSocket.lastTranscription]);

  // Handle WebSocket status messages
  useEffect(() => {
    if (webSocket.lastMessage) {
      const message = webSocket.lastMessage;
      
      if (message.type === 'status') {
        if (message.status === 'connected' && roomId) {
          setIsJoined(true);
        } else if (message.status === 'disconnected') {
          setIsJoined(false);
        }
      }
    }
  }, [webSocket.lastMessage, roomId]);

  // Join room function
  const handleJoinRoom = useCallback(async () => {
    const targetRoomId = inputRoomId.trim().toUpperCase();
    if (!targetRoomId) return;

    try {
      setRoomId(targetRoomId);
      setSearchParams({ room: targetRoomId });
      
      // Clear existing subtitles
      setSubtitles([]);
      
      // Clear existing timeouts
      subtitleTimeoutRef.current.forEach(timeout => clearTimeout(timeout));
      subtitleTimeoutRef.current.clear();
      
    } catch (error) {
      console.error('Failed to join room:', error);
    }
  }, [inputRoomId, setSearchParams]);

  // Auto-connect when roomId changes
  useEffect(() => {
    if (roomId && !webSocket.isConnected && webSocket.status !== 'connecting') {
      webSocket.connect().catch(console.error);
    }
  }, [roomId, webSocket]);

  // Leave room function
  const handleLeaveRoom = useCallback(() => {
    webSocket.disconnect();
    setRoomId('');
    setInputRoomId('');
    setIsJoined(false);
    setSubtitles([]);
    setSearchParams({});
    
    // Clear timeouts
    subtitleTimeoutRef.current.forEach(timeout => clearTimeout(timeout));
    subtitleTimeoutRef.current.clear();
  }, [webSocket, setSearchParams]);

  // Manual reconnect
  const handleReconnect = useCallback(() => {
    if (roomId) {
      webSocket.connect().catch(console.error);
    }
  }, [roomId, webSocket]);

  // Connection status indicator
  const getConnectionStatus = () => {
    if (webSocket.status === 'connected') {
      return { icon: Wifi, color: 'text-green-500', text: 'Connected', variant: 'default' as const };
    } else if (webSocket.status === 'connecting' || webSocket.status === 'reconnecting') {
      return { icon: RefreshCw, color: 'text-yellow-500', text: 'Connecting...', variant: 'secondary' as const };
    } else if (webSocket.status === 'error') {
      return { icon: WifiOff, color: 'text-red-500', text: 'Connection Error', variant: 'destructive' as const };
    } else {
      return { icon: WifiOff, color: 'text-gray-500', text: 'Disconnected', variant: 'secondary' as const };
    }
  };

  const connectionStatus = getConnectionStatus();
  const ConnectionIcon = connectionStatus.icon;

  // Format timestamp for display
  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  // Calculate subtitle age for styling
  const getSubtitleAge = (displayTime: number) => {
    const age = (Date.now() - displayTime) / 1000;
    if (age < 3) return 'new';
    if (age < 7) return 'medium';
    return 'old';
  };

  return (
    <div className={cn("max-w-4xl mx-auto p-6 space-y-6", className)}>
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">Audio Viewer</h1>
        <p className="text-muted-foreground">
          Join a live audio stream to see real-time transcriptions
        </p>
      </div>

      {!roomId ? (
        /* Room Join Interface */
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LogIn className="h-5 w-5" />
              Join Room
            </CardTitle>
            <CardDescription>
              Enter the Room ID provided by the streamer to join their live audio stream
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <label htmlFor="roomId" className="text-sm font-medium">Room ID</label>
                <input
                  id="roomId"
                  type="text"
                  value={inputRoomId}
                  onChange={(e) => setInputRoomId(e.target.value.toUpperCase())}
                  placeholder="Enter 8-character room ID"
                  className="w-full mt-1 px-3 py-2 border border-input rounded-md bg-background font-mono text-lg tracking-wider uppercase"
                  maxLength={8}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleJoinRoom();
                    }
                  }}
                />
              </div>
              <div className="flex items-end">
                <Button 
                  onClick={handleJoinRoom}
                  disabled={!inputRoomId.trim()}
                  size="lg"
                >
                  <LogIn className="h-4 w-4 mr-2" />
                  Join Room
                </Button>
              </div>
            </div>
            
            <div className="text-sm text-muted-foreground">
              Room IDs are 8 characters long and contain letters and numbers (e.g., ABC12345)
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Room Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Viewing Room: {roomId}
              </CardTitle>
              <CardDescription>
                {isJoined ? 'Connected to live stream' : 'Connecting to stream...'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Connection Status:</span>
                <Badge variant={connectionStatus.variant}>
                  <ConnectionIcon className={cn("h-3 w-3 mr-1", connectionStatus.color, {
                    'animate-spin': webSocket.status === 'connecting' || webSocket.status === 'reconnecting'
                  })} />
                  {connectionStatus.text}
                </Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Room ID:</span>
                <code className="px-2 py-1 bg-muted rounded font-mono text-sm">
                  {roomId}
                </code>
              </div>

              {webSocket.error && (
                <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                  <div className="text-sm">
                    <div className="font-medium text-destructive">Connection Error</div>
                    <div className="text-destructive/80">{webSocket.error.message}</div>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={handleReconnect}
                  variant="outline"
                  disabled={webSocket.status === 'connecting' || webSocket.status === 'reconnecting'}
                  className="flex-1"
                >
                  <RefreshCw className={cn("h-4 w-4 mr-2", {
                    'animate-spin': webSocket.status === 'connecting' || webSocket.status === 'reconnecting'
                  })} />
                  Reconnect
                </Button>
                <Button
                  onClick={handleLeaveRoom}
                  variant="outline"
                  className="flex-1"
                >
                  Leave Room
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Subtitles Display */}
          <Card className="min-h-[400px]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Live Transcription
              </CardTitle>
              <CardDescription>
                Real-time subtitles will appear here as the streamer speaks
              </CardDescription>
            </CardHeader>
            <CardContent>
              {subtitles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                    <Users className="h-8 w-8 text-muted-foreground" />
                  </div>
                  <div className="text-lg font-medium text-muted-foreground mb-2">
                    {isJoined ? 'Waiting for audio...' : 'Connecting to stream...'}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {isJoined 
                      ? 'Subtitles will appear here when the streamer starts speaking'
                      : 'Please wait while we connect you to the live stream'
                    }
                  </div>
                </div>
              ) : (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {subtitles.map((subtitle) => {
                    const age = getSubtitleAge(subtitle.displayTime);
                    return (
                      <div
                        key={subtitle.id}
                        className={cn(
                          "p-4 rounded-lg border transition-all duration-500",
                          {
                            'bg-primary/10 border-primary/20': age === 'new',
                            'bg-muted/50 border-muted': age === 'medium',
                            'bg-muted/30 border-muted/50 opacity-70': age === 'old'
                          }
                        )}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="text-base leading-relaxed">
                              {subtitle.text}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {formatTimestamp(subtitle.timestamp)}
                            </div>
                            {subtitle.confidence < 0.8 && (
                              <Badge variant="secondary" className="text-xs">
                                Low confidence
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Instructions Card */}
          <Card>
            <CardHeader>
              <CardTitle>How to View</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                  1
                </div>
                <div>You're now connected to the live audio stream</div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                  2
                </div>
                <div>Subtitles will appear automatically as the streamer speaks</div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                  3
                </div>
                <div>Each subtitle shows for 10 seconds and includes a timestamp</div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                  4
                </div>
                <div>If connection is lost, the system will automatically try to reconnect</div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};